import { Space, Text } from '@procurify/ui'

import { useProcurifyIntl, useFormatBudgetValues } from '@webapp/hooks'
import { type IFixedBudgetSubtotal } from '@webapp/hooks/useFormatBudget'
import { BudgetProgressBarTooltipStyled } from './styles/BudgetProgressBarTooltipStyled'
import messages from '../../messages'
import { BudgetCategoryCardSubtotal } from '../BudgetCategoryCardSubtotal/BudgetCategoryCardSubtotal'

export interface IBudgetProgressBarTooltipProps {
  isOverBudget: boolean
  committedSpend: IFixedBudgetSubtotal
  pendingRequests: IFixedBudgetSubtotal
  remainingAfterPendingRequests: IFixedBudgetSubtotal
  spendRequest?: IFixedBudgetSubtotal
  totalSpend: IFixedBudgetSubtotal
  large?: boolean
}

export const BudgetProgressBarTooltip = ({
  isOverBudget,
  committedSpend,
  pendingRequests,
  remainingAfterPendingRequests,
  totalSpend,
  spendRequest,
  large,
}: IBudgetProgressBarTooltipProps) => {
  const intl = useProcurifyIntl()
  const { formatBudgetPercent, formatBudgetPrice } = useFormatBudgetValues()

  return (
    <BudgetProgressBarTooltipStyled large={large}>
      <Space size='small'>
        <Space.Item>
          <BudgetCategoryCardSubtotal
            label={intl.formatMessage(messages.subtotalsCommittedLabel)}
            amount={committedSpend.amount}
            percentage={committedSpend.percentage.amount}
            indicatorColor={
              isOverBudget ? 'budgets.overBudget' : 'budgets.committed'
            }
            shouldBoldAmount
            formatSpacing={{
              rowGap: 'xsmall',
            }}
          />
        </Space.Item>
        {spendRequest && (
          <Space.Item>
            <BudgetCategoryCardSubtotal
              label={intl.formatMessage(messages.thisRequestLabel)}
              amount={spendRequest.amount}
              percentage={spendRequest.percentage.amount}
              indicatorColor={
                isOverBudget ? 'budgets.overBudget' : 'budgets.committed'
              }
              shouldBoldAmount
              indicatorStriped
              formatSpacing={{
                rowGap: 'xsmall',
              }}
            />
          </Space.Item>
        )}
        <Space.Item>
          <BudgetCategoryCardSubtotal
            label={intl.formatMessage(messages.subtotalsPendingLabel)}
            amount={pendingRequests.amount}
            percentage={pendingRequests.percentage.amount}
            indicatorColor='budgets.pending'
            shouldBoldAmount
            formatSpacing={{
              rowGap: 'xsmall',
            }}
          />
        </Space.Item>
        <Space.Item>
          <BudgetCategoryCardSubtotal
            label={intl.formatMessage(
              messages.subtotalsRemainingAfterRequestsLabel
            )}
            amount={remainingAfterPendingRequests.amount}
            percentage={remainingAfterPendingRequests.percentage.amount}
            indicatorColor='budgets.available'
            shouldBoldAmount
            formatSpacing={{
              rowGap: 'xsmall',
            }}
          />
        </Space.Item>
        <Space.Item>
          <Text align='left'>
            {intl.formatMessage(messages.subtotalsProgressBarTooltip, {
              total: (
                <strong>
                  {formatBudgetPrice(totalSpend.amount.toString())}
                </strong>
              ),
              percentage: formatBudgetPercent(
                totalSpend.percentage.amount.toString()
              ),
            })}
          </Text>
        </Space.Item>
      </Space>
    </BudgetProgressBarTooltipStyled>
  )
}
